import { KeepaQueryJSON } from "@boxem/core-api-schema/lib/keepa";
import { SearchableParams, SearchableParamType, SearchableParam, isNumericType, SearchableParamsEntry } from "./searchable-params";


export type BuiltQuery = Partial<typeof SearchableParams>;
export type BuiltQueryKeepa = Partial<KeepaQueryJSON>;


export function parseQueryKeepa(query: string): { parseError?: ParseError, builtQuery?: BuiltQueryKeepa } {
  const intermediateResult = parseQuery(query);
  if (intermediateResult.parseError) {
    return {
      parseError: intermediateResult.parseError,
    };
  }

  return queryToKeepa(intermediateResult.builtQuery);
}

export function queryToKeepa(builtQuery: BuiltQuery[]): { parseError?: ParseError, builtQuery?: BuiltQueryKeepa } {
  // we have to convert our format to keepa's.
  const keepa = new KeepaQueryJSON();
  for (const query of builtQuery) {
    for (const [key, value] of safeEntries(query)) {
      switch (value.operationNode.token.type) {
        case TokenType.equals: {
          const gteKey = key + "_gte";
          const lteKey = key + "_lte";
          if (keepa.hasOwnProperty(gteKey) && keepa.hasOwnProperty(lteKey)) {
            // equality is encoded as lte + gte on this field:
            keepa[gteKey] = value.value;
            keepa[lteKey] = value.value;

          } else {
            // equality is encoded as just the field being exactly equal to this value:
            keepa[key] = value.value;
          }
        } break;

        case TokenType.notEquals: {
          const gteKey = key + "_gte";
          const lteKey = key + "_lte";
          if (keepa.hasOwnProperty(gteKey) && keepa.hasOwnProperty(lteKey)) {
            // inequality can be encoded hackily as lte + gte on this field:
            const v = value.value as number;
            keepa[gteKey] = v+1;
            keepa[lteKey] = v-1;

          } else {
            // inequality is not supported! no way to do this with keepa.
            // @TODO error or something
            return  {
              // parseError: {
              //   type: ParseErrorType.operandTypeIsWrong,
              //   index: 
              // },
            };
          }
        } break;

        case TokenType.greaterThan: {
          const v = value.value as number;
          keepa[key + "_gte"] = v+1;
        } break;

        case TokenType.lessThan: {
          const v = value.value as number;
          keepa[key + "_lte"] = v-1;
        } break;
          
        case TokenType.greaterThanOrEquals: {
          keepa[key + "_gte"] = value.value;
        } break;

        case TokenType.lessThanOrEquals: {
          keepa[key + "_lte"] = value.value;
        } break;

        case TokenType.not: {
          // @TODO booleans
        } break;

        // case TokenType.and:
        // case TokenType.or:
        // case TokenType.xor:
      }
    }
  }

  if (Object.keys(keepa).length === 0) {
    return {
      parseError: {
        type: ParseErrorType.emptyExpression,
        index: 0,
        span: 1,
        message: "Query is empty",
      },
    };
  }

  if (true) { // @TODO verbose flag?
    console.log("Built keepa query: ");
    for (const [key, value] of safeEntries(keepa)) {
      console.log(`param: '${key}', value: '${value}'`);
    }
  }

  return {
    builtQuery: keepa,
  };
}

export const MAX_QUERY_LENGTH = 512;

export function sanitizeQuery(query: string): { parseError?: ParseError, sanitizedQuery?: string } {
  // check null/min length
  if (!query || query.trim().length === 0) {
    return {
      parseError: {
        type: ParseErrorType.queryIsEmpty,
        index: -1,
        span: -1,
      },
    };
  }

  // max length:
  if (query.length > MAX_QUERY_LENGTH) {
    return {
      parseError: {
        type: ParseErrorType.queryTooLong,
        index: -1,
        span: -1,
      },
    };
  }

  // ensure it's a single-line (queries cannot be multi-line):
  const newlineIndex = query.indexOf("\n")
  if (newlineIndex !== -1) {
    return {
      parseError: {
        type: ParseErrorType.queryContainsNewline,
        index: newlineIndex,
        span: 1,
      },
    };
  }

  // ensure it has no invalid chars:
  for (let i = 0; i < query.length; i++) {
    if (!isValidQueryCharacter(query, i)) {
      return {
        parseError: reportError(
          ParseErrorType.invalidCharacter,
          i,
          1,
          "We found a weird character here that we don't support. Did you paste in some Emojis, or non-english text?"
        ),
      };
    }
  }

  // handle white-space. we want to trim the white-space from the ends, then replace all spans of two or more
  // white-space chars with a single space. This allows us to reliably cache queries that are identical by their
  // string input, whereas otherwise "salesRank > 20" and "salesRank   >    20" would be considered different.
  return {
    sanitizedQuery: query.trim().replace(/\s\s+/g, " "),
  };
}

export function parseQuery(query: string): { parseError?: ParseError, builtQuery?: BuiltQuery[] } {
  // @NOTE we discard the 'sanitizedQuery' result here and continue with just 'query',
  // because we want to keep the original form of the query so our error messages can
  // correctly point to the error location in the source string (sanitizing a query string
  // may change its length/move stuff around). The actual sanitized query as a string is
  // only ever used as a key in a cache.
  const { parseError } = sanitizeQuery(query);
  if (parseError) {
    return {
      parseError,
    };
  }

  ///////////////////////////////////////////////////
  //
  // TOKENIZER/LEXER
  //
  //
  const tokens: Token[] = [];
  for (let i = 0; i < query.length; i++) {
    let type = TokenType.invalid;
    let length = 1;
    let start = i;

    if (isWhitespace(query, i)) {
      continue;

    } else if (isDigit(query, i)) {
      type = TokenType.number;
      let hasRadixPoint = false;
      do {
        i++;

        if (query[i] === "." && !hasRadixPoint) {
          hasRadixPoint = true;

        } else if (!isDigit(query, i)) {
          break;
        }

        length++;

      } while (i < query.length);

    } else if (query[i] === "\"" || query[i] === "'") {
      type = TokenType.string;
      const intialQuotemark = i;

      let foundTerminatingQuotemark = false;
      do {
        i++;

        if (query[i] === query[intialQuotemark]) {
          length++; // the terminating quotemark is technically part of the string in our rules.
          foundTerminatingQuotemark = true;
          break;
        }

        // @TODO ensure we handle escaped quotes and backslashes
        if (query[i] === "\\") {
          if (query[i+1] === query[intialQuotemark]) {

          } else if (query[i+1] === "\\") {
            // it's a literal backslash
          }
        }

        length++;

      } while (i < query.length);

      if (!foundTerminatingQuotemark) {
        return {
          parseError: reportError(
            ParseErrorType.missingTerminatingQuote,
            start,
            1,
            "Failed to find a closing quotemark for a string! Single and double-quotes must be in pairs. If you want to put a literal quote that doesn't end the expression, you can put a backslash (\\) before the quote."
          ),
        };
      }
    } else if (query[i] === "-") {
      type = TokenType.minus;

    } else if (query[i] === "!") {
      type = TokenType.not;

    } else if (query[i] === "[") {
      type = TokenType.openBracket;

    } else if (query[i] === "]") {
      type = TokenType.closeBracket;

    } else if (query[i] === "(") {
      type = TokenType.openParens;

    } else if (query[i] === ")") {
      type = TokenType.closeParens;

    } else if (query[i] === "=") {
      type = TokenType.equals;

    } else {
      type = TokenType.symbol;
      // expect a keyword/query/symbol/operator. these are like strings but without the quotes around them.
      // we treat them as same, except the terminal is now any whitespace.
      do {
        i++;

        if (isWhitespace(query, i)) {
          break;
        }

        length++;

      } while (i < query.length);

      // check if we have an operator:
      const sub = query.substring(start, start+length);
      const operator = TokenMap[sub]
      if (operator) {
        type = operator;
      }
    }

    tokens.push({
      type,
      index: start,
      length,
    });
  }

  ///////////////////////////////////////////////////
  //
  // PARSER
  //
  //

  function parseOperationIntoExpression(es: ASTNode[], os: ASTNode[], tokens: Token[]): ParseError | undefined {
    if (os.length === 0) throw "Trying to parse operation into expression but there's no operator on the stack...";

    const op = os.pop();
    if (op.isUnary) {
      const child = es.pop();
      if (!child) {
        return reportError(
          ParseErrorType.missingOperandUnary,
          op.token.index,
          op.token.length,
          "Missing an operand for this operator!" // @TODO better error message
        );
      }
      op.children.push(child);

    } else {
      const rhs = es.pop();
      if (!rhs) {
        return reportError(
          ParseErrorType.missingOperandBinary,
          op.token.index,
          op.token.length,
          `Missing a left-hand side for the '${getTokenString(query, op.token)}'. We expect a name of a product property, like 'salesRank'`,
        );
      }
      const lhs = es.pop();
      if (!lhs) {
        // if we're missing a lhs, this actually means the 'rhs' is missing in most cases
        // (because the rhs will take the place of the lhs)
        return reportError(
          ParseErrorType.missingOperandBinary,
          op.token.index,
          op.token.length,
          // @TODO based on rhs (which is actually the lhs) infer what value type we want to see on the rhs
          `Missing a right-hand side for the '${getTokenString(query, op.token)}'`, 
        );
      }
      op.children.push(lhs, rhs);

      // @TODO unwrap commas for arrays...
    }
    es.push(op);
  }

  enum Associativity { none, leftToRight, rightToLeft };
  function associativity(node: ASTNode): Associativity {
    switch (node.token.type) {
      case TokenType.minus:
        if (node.isUnary) return Associativity.rightToLeft;
    }
    // @NOTE(nick) if we had statements or assignment expresisons etc. we might want a 'none' associativity...
    return Associativity.leftToRight;
  }

  function precedence(node: ASTNode): number {
    switch (node.token.type) {
      default:
      case TokenType.invalid:
      case TokenType.symbol:
      case TokenType.number:
      case TokenType.string:
      case TokenType.closeBracket:
      case TokenType.closeParens:
        throw `Trying to get precedence of non-operator! ${node}`;

      // case TokenType.openBracket:
      // case TokenType.openParens:
        // return 0;

      case TokenType.and:
      case TokenType.or:
        return 2;

      case TokenType.equals:
      case TokenType.notEquals:
      case TokenType.greaterThan:
      case TokenType.greaterThanOrEquals:
      case TokenType.lessThan:
      case TokenType.lessThanOrEquals:
        return 3;

      case TokenType.minus:
        return node.isUnary ? 6 : 4;

      case TokenType.not:
        return 6;
    }
  }

  function canPopAndApply(os: ASTNode[], node: ASTNode): boolean {
    if (os.length === 0) return false;

    const top = os[os.length-1];
    const assoc = associativity(node);
    if (assoc == Associativity.leftToRight) {
      return precedence(node) <= precedence(top);

    } else if (assoc === Associativity.rightToLeft) {
      return precedence(node) < precedence(top);

    } else {
      return false;
    }
  }

  // below here is basically just the shunting-yard algorithm (https://en.wikipedia.org/wiki/Shunting_yard_algorithm)
  const es: ASTNode[] = []; // es === expression stack
  const os: ASTNode[] = []; // os === operator stack
  let i = 0;
  while (i < tokens.length) {
    const token = tokens[i];
    switch (token.type) {
      case TokenType.closeBracket:
      case TokenType.closeParens: {
        const delimiter = token.type;
        while (os.length !== 0) {
          if (os[os.length-1].token.type === delimiter) break;
          const error = parseOperationIntoExpression(es, os, tokens);
          if (error) {
            return {
              parseError: error,
            };
          }
        }

        if (os.length === 0) {
          let errorType, message;
          if (delimiter === TokenType.closeBracket) {
            errorType = ParseErrorType.missingClosingBracket;
            message = "Missing closing bracket";
          } else {
            errorType = ParseErrorType.missingClosingParentheses;
            message = "Missing closing parens";
          }
          return {
            parseError: reportError(errorType, token.index, token.length, message),
          };
        }

        os.pop(); // discard open parens/bracket

      } break;

      case TokenType.symbol:
      case TokenType.number:
      case TokenType.string: {
        es.push({ token, children: [] });
      } break;

      default: {
        // these should all be operators
        const node: ASTNode = { token, children: [] };

        // resolve unary-ness. (if the previous token is an operator, we're unary)
        if (i < 1 || tokenTypeIsOperator(tokens[i-1].type)) {
          switch (node.token.type) {
            case TokenType.minus:
            case TokenType.not:
              node.isUnary = true;
              break;
          }
        }

        while (canPopAndApply(os, node)) {
          const error = parseOperationIntoExpression(es, os, tokens);
          if (error) {
            return {
              parseError: error,
            };
          }
        }
        os.push(node);
      } break;
    }

    i++;
  }

  while (os.length !== 0) {
    const top = os[os.length-1];
    const error = parseOperationIntoExpression(es, os, tokens);
    if (error) {
      return {
        parseError: error,
      };
    }
  }

  if (es.length === 0) {
    return {
      parseError: reportError(
        ParseErrorType.emptyExpression,
        0,
        0,
        "Empty expression",
      ),
    };
  }

  const program = es.pop();

  // naked expressions just means expressions that we didn't get an operator assigned to.
  // these will implicitly be treated as a 'title' or keyword search.
  const nakedExpressions: ASTNode[] = [];
  for (let i = 0; i < es.length; i++) {
    nakedExpressions.push(es[i]);
  }
  if (!tokenTypeIsOperator(program.token.type)) {
    nakedExpressions.push(program);
  }

  console.log(prettyPrintASTNode(query, program, "Query:\n"));

  ///////////////////////////////////////////////////
  //
  // TYPECHECKING/QUERY GENERATION
  // this is the really messy stuff...
  //

  const queries: Partial<BuiltQuery>[] = [];

  // decay the unary operator until we get the thing it is unary-ing.
  // if you had a structure like:
  //    '-'
  //      \
  //       '-'
  //         \ 
  //          '-'
  //            \
  //             '42'
  // 
  // we'd return you the node for '42', with the 'decayCount' set to 3, which in this case
  // indicates that the '42' is a negative number.
  function decayUnaryOperatorToUnderlyingASTNode(node: ASTNode): { decayCount: number, n: ASTNode } {
    const result = { n: node, decayCount: 0 };
    while (result.n.children.length > 0 && (result.n.token.type === TokenType.minus && result.n.isUnary)) {
      result.decayCount++;
      result.n = result.n.children[0];
    }
    return result;
  }

  // @TODO date fields, being involved in a comparison with '<', '>' '<=' or '>='.
  function handleBinaryOperator(query: string, node: ASTNode) {
    const comparisonOperatorString = getTokenString(query, node.token);
    const lhs = node.children[0];
    if (lhs.token.type !== TokenType.string && lhs.token.type !== TokenType.symbol) {
      return {
        type: ParseErrorType.operandTypeIsWrong,
        index: lhs.token.index,
        span: lhs.token.length,
        message: `The left-hand side of a '${comparisonOperatorString}' comparison should be a name of a product property that is a number or date, like 'salesRank'`,
      };
    }
    const lhsSearchParam = lookupTokenInSearchableParams(query, lhs.token);
    if (!lhsSearchParam) {
      return {
        type: ParseErrorType.unknownPhraseWhenExpectingSearchableParam,
        index: lhs.token.index,
        span: lhs.token.length,
        message: `The left-hand side of a '${comparisonOperatorString}' comparison should be a name of a product property that is a number or date, like 'salesRank'`,
      };
    }

    if (lhsSearchParam.value.type === SearchableParamType.array) {
      // @TODO actually handle arrays. for now, we'll just decay the array to its underlying type and expect one value.
      switch (lhsSearchParam.value.arrayType) {
        case SearchableParamType.boolean:
          lhsSearchParam.value = new SearchableParam<boolean>({ type: SearchableParamType.boolean });
          break;
        case SearchableParamType.byte:
          lhsSearchParam.value = new SearchableParam<number>({ type: SearchableParamType.byte });
          break;
        case SearchableParamType.integer:
          lhsSearchParam.value = new SearchableParam<number>({ type: SearchableParamType.integer });
          break;
        case SearchableParamType.long:
          lhsSearchParam.value = new SearchableParam<number>({ type: SearchableParamType.long });
          break;
        case SearchableParamType.string:
          lhsSearchParam.value = new SearchableParam<string>({ type: SearchableParamType.string });
          break;
        case SearchableParamType.date:
          lhsSearchParam.value = new SearchableParam<number>({ type: SearchableParamType.date });
          break;
        // case SearchableParamType.array:
      }
    }

    if (isNumericType(lhsSearchParam.value.type)) {
      let rhs = node.children[1];
      let negativeFlag = false;
      const decayResult = decayUnaryOperatorToUnderlyingASTNode(rhs);
      if (decayResult.decayCount % 2 === 1) {
        negativeFlag = true;
      }
      rhs = decayResult.n;

      if (rhs.token.type !== TokenType.number) {
        return {
          type: ParseErrorType.operandTypeIsWrong,
          index: rhs.token.index,
          span: rhs.token.length,
          message: `The right-hand side of a '${comparisonOperatorString}' should be a number, like 1000, because the left-hand side (${getTokenString(query, lhs.token)}) is a property that is a number.`,
        };
      }
  
      let numericValue = parseFloat(getTokenString(query, rhs.token));
      if (isNaN(numericValue)) {
        return {
          type: ParseErrorType.operandTypeIsWrong,
          index: rhs.token.index,
          span: rhs.token.length,
          message: `The right-hand side of a '${comparisonOperatorString}' should be a number, like 1000.`,
        };
      }

      // @TODO validate size of number
      // @TODO warn about rounding? it was simplest to just Math.floor(...) the value...
      // we think all values are ints right now, but maybe we want floats for some price stuff?
      if (negativeFlag) numericValue = -numericValue;
      const param =  lhsSearchParam.value.concretize(node, Math.floor(numericValue));
      const newQuery = {} as Partial<BuiltQuery>;
      newQuery[lhsSearchParam.key] = param as unknown as any; // @TODO fix typerror (the LHS could be any searhable param type)
      queries.push(newQuery);

    } else if (lhsSearchParam.value.type === SearchableParamType.string) {
      let rhs = node.children[1];
      if (rhs.token.type !== TokenType.string && rhs.token.type !== TokenType.symbol) {
        return {
          type: ParseErrorType.operandTypeIsWrong,
          index: lhs.token.index,
          span: lhs.token.length,
          message: `The right-hand side of a '${comparisonOperatorString}' comparison should be a string, because the left-hand side is a product property which we expect to be a string (${getTokenString(query, lhs.token)}), like "White Socks". We got '${getTokenString(query, rhs.token)}'. Maybe you meant to enclose it in quotes?`,
        };
      }

      const param =  lhsSearchParam.value.concretize(node, trimQuotesIfString(query, rhs.token));  
      const newQuery = {} as Partial<BuiltQuery>;  
      newQuery[lhsSearchParam.key] = param as unknown as any; // @TODO fix typerror (the LHS could be any searhable param type)
      queries.push(newQuery);

    } else if (lhsSearchParam.value.type === SearchableParamType.boolean) {
      let rhs = node.children[1];
      if (rhs.token.type !== TokenType.true && rhs.token.type !== TokenType.false) {
        return {
          type: ParseErrorType.operandTypeIsWrong,
          index: lhs.token.index,
          span: lhs.token.length,
          message: `The right-hand side of a '${comparisonOperatorString}' comparison should be a boolean ('true' or 'false'), because the left-hand side is a product property which we expect to be a true or false value (${getTokenString(query, lhs.token)}).`,
        };
      }

      const param =  lhsSearchParam.value.concretize(node, trimQuotesIfString(query, rhs.token));
      const newQuery = {} as Partial<BuiltQuery>;  
      newQuery[lhsSearchParam.key] = param as unknown as any; // @TODO fix typerror (the LHS could be any searhable param type)
      queries.push(newQuery);
    } 
  }

  const typecheckOrQueryGenError = traverse(program, (node: ASTNode): ParseError | void => {
    switch (node.token.type) {
      case TokenType.symbol:
      case TokenType.string:
      case TokenType.invalid:
      case TokenType.number:
      case TokenType.minus:
      case TokenType.not:
      case TokenType.and:
      case TokenType.or:
      case TokenType.xor:
      case TokenType.openBracket:
      case TokenType.closeBracket:
      case TokenType.openParens:
      case TokenType.closeParens:
        // @NOTE nothing to do with these for now...
        break;

      case TokenType.equals:
      case TokenType.notEquals:
      case TokenType.greaterThan:
      case TokenType.greaterThanOrEquals:
      case TokenType.lessThan:
      case TokenType.lessThanOrEquals: {
        const error = handleBinaryOperator(query, node);
        if (error) return error;
      } break;
    }
  });

  // naked expr can be boolean expr if they match a boolean property, check for that:
  for (let i = 0; i < nakedExpressions.length;) {
    const expr = nakedExpressions[i];
    const sp = lookupTokenInSearchableParams(query, expr.token);
    if (sp && sp.value.type === SearchableParamType.boolean) {
      const param = new SearchableParam<boolean>({ type: SearchableParamType.boolean });
      // @HACK there's no actual operation token in this case...
      param.operationNode = {
        token: {
          index: -1,
          length: -1,
          type: TokenType.equals,
        },
        children: [],
      }
      param.value = true;
      const newQuery = {} as Partial<BuiltQuery>;  
      newQuery[sp.key] = param as unknown as any; // @TODO fix typerror (the LHS could be any searhable param type)
      queries.push(newQuery);

      nakedExpressions.shift();
      continue;
    }
    i++;
  }

  // as a hack, if we have naked expressions, we'll append the naked expression to a 'title' search param.
  // This is because you probably want to just search by keywords (which is what title is) if you just type,
  // say, "socks" into the search box and hit enter.
  if (nakedExpressions.length > 0 || !tokenTypeIsOperator(program.token.type)) {
    let titleQueryExtras = nakedExpressions.map(n => getTokenString(query, n.token)).join(" ");
    if (!tokenTypeIsOperator(program.token.type)) {
      titleQueryExtras += " " + getTokenString(query, program.token);
    }

    let param = queries.find(q => !!q["title"])?.["title"];
    if (!!param) {
      param.value += titleQueryExtras;

    } else {
      param = new SearchableParam<string>({ type: SearchableParamType.string });
      // @HACK there's no actual operation token in this case...
      param.operationNode = {
        token: {
          index: -1,
          length: -1,
          type: TokenType.equals,
        },
        children: [],
      }
      param.value = titleQueryExtras;
      const newQuery = {} as Partial<BuiltQuery>;  
      newQuery["title"] = param;
      queries.push(newQuery);
    }
  }

  if (typecheckOrQueryGenError) {
    return { parseError: typecheckOrQueryGenError };
  }

  return { builtQuery: queries };
}

export enum ParseErrorType {
  none,
  invalidCharacter,
  queryContainsNewline,
  queryIsEmpty,
  queryTooLong,
  missingTerminatingQuote,
  missingClosingParentheses,
  missingClosingBracket,
  emptyExpression,
  missingOperandUnary,
  missingOperandBinary,
  operandTypeIsWrong,
  unknownPhraseWhenExpectingSearchableParam,
}

export interface ParseError {
  type: ParseErrorType;
  index: number;
  span: number;
  message?: string;
}

function reportError(type: ParseErrorType, index: number, span: number = 1, message?: string): ParseError {
  return { type, index, span, message };
}

export enum TokenType {
  invalid,

  symbol,
  number,
  string,

  minus,               // '-'
  equals,              // '='
  notEquals,           // '!='
  greaterThan,         // '>'
  greaterThanOrEquals, // '>='
  lessThan,            // '<'
  lessThanOrEquals,    // '<='

  not,                 // '!' or 'not'
  and,                 // 'and' or '&'
  or,                  // 'or' or '|'
  xor,                 // 'xor' or '^'

  true,
  false,

  openBracket,
  closeBracket,
  openParens,
  closeParens,
}

function tokenTypeIsOperator(tt: TokenType): boolean {
  switch (tt) {
    case TokenType.minus:

    case TokenType.equals:
    case TokenType.notEquals:
    case TokenType.not:
    case TokenType.greaterThan:
    case TokenType.greaterThanOrEquals:
    case TokenType.lessThan:
    case TokenType.lessThanOrEquals:

    case TokenType.and:
    case TokenType.or:
      return true;
  }
  return false;
}

function tokenTypeToString(tt: TokenType, isUnary?: boolean): string {
  switch (tt) {
    case TokenType.invalid:
      break;

    case TokenType.symbol:
      return "(SYMBOL)";
    case TokenType.number:
      return "(NUMBER)";
    case TokenType.string:
      return "(STRING)";
    case TokenType.minus:
      return isUnary ? "(OPERATOR 'UNARY MINUS')" : "(OPERATOR 'MINUS')";
    case TokenType.equals:
      return "(OPERATOR 'EQUALS')";
    case TokenType.notEquals:
      return "(OPERATOR 'NOT EQUALS')";
    case TokenType.not:
      return "(OPERATOR 'NOT')";
    case TokenType.greaterThan:
      return "(OPERATOR 'GREATER THAN')";
    case TokenType.greaterThanOrEquals:
      return "(OPERATOR 'GREATER THAN OR EQUALS')";
    case TokenType.lessThan:
      return "(OPERATOR 'LESS THAN')";
    case TokenType.lessThanOrEquals:
      return "(OPERATOR 'LESS THAN OR EQUALS')";
    case TokenType.and:
      return "(OPERATOR 'LOGICAL AND')";
    case TokenType.or:
      return "(OPERATOR 'LOGICAL OR')";
    case TokenType.openBracket:
      return "(OPERATOR 'OPEN BRACKET')";      
    case TokenType.closeBracket:
      return "(OPERATOR 'CLOSE BRACKET')";     
    case TokenType.openParens:
      return "(OPERATOR 'OPEN PARENS')";   
    case TokenType.closeParens:
      return "(OPERATOR 'CLOSE PARENS')";   
  }
}

const TokenMap: {[key: string]: TokenType} = {
  "-":   TokenType.minus,

  // equality:
  "is":  TokenType.equals,
  "=":   TokenType.equals,
  "!=":  TokenType.notEquals,

  // comparison:
  ">":   TokenType.greaterThan,
  ">=":  TokenType.greaterThanOrEquals,
  "<":   TokenType.lessThan,
  "<=":  TokenType.lessThanOrEquals,

  // logical operators:
  "not": TokenType.not,
  // "!":   TokenType.not,
  "and": TokenType.and,
  // "&":   TokenType.and,
  "or":  TokenType.or,
  // "|":   TokenType.or,
  "xor": TokenType.xor,
  // "^"  : TokenType.xor,

  // "[":   TokenType.openBracket,
  // "]":   TokenType.closeBracket,
  // "(":   TokenType.openParens,
  // ")":   TokenType.closeParens,
};

export interface Token {
  type: TokenType;
  index: number; // index into the original query
  length: number; // length of this token
}

export interface ASTNode {
  token: Token;
  children: ASTNode[];
  isUnary?: boolean;
}

// if the callback returns an error, we stop iteration.
function traverse(node: ASTNode, callback: (n: ASTNode) => ParseError | void): ParseError | void {
  for (let i = 0; i < node.children.length; i++) {
    const error = traverse(node.children[i], callback);
    if (error) return error;
  }
  return callback(node);
}

function printToken(query: string, token: Token) {
  console.info(getTokenString(query, token));
}

function getTokenString(query: string, token: Token): string {
  return query.substring(token.index, token.index+token.length);
}

function nodeToString(query: string, node: ASTNode): string {
  return tokenTypeToString(node.token.type, node.isUnary) + " " + getTokenString(query, node.token) + "\n";
}

function prettyPrintASTNode(query: string, node: ASTNode, s: string = "", indent: string = "", lastChild: boolean = false): string {
  let str = s;
  if (!node) {
    str += "null\n";
    return str;
  }

  str += indent;
  if (lastChild) {
    str += "\\-";
    indent += " ";

  } else {
    str += "|-";
    indent += "| ";
  }
  str += nodeToString(query, node);

  for (let i = 0; i < node.children.length; i++) {
    str = prettyPrintASTNode(query, node.children[i], str, indent, i === (node.children.length-1));
  }

  return str;
}

function isWhitespace(query: string, i: number): boolean {
  const a = query.charCodeAt(i);
  return a === 9 || a === 10 || a === 13 || a === 32; // tab, newline, carriage return, space. we don't handle form feed or vertical tab right now
}

function isAlpha(query: string, i: number): boolean {
  const a = query.charCodeAt(i);
  return (a >= 65 && a <= 90) || (a >= 97 && a <= 122);
}

function isDigit(query: string, i: number): boolean {
  const a = query.charCodeAt(i);
  return a >= 48 && a <= 57;
}

// unicode is omitted, but we technically support unicode as long as its enclosed in quotes...
function isValidQueryCharacter(query: string, i: number): boolean {
  const a = query.charCodeAt(i);
  return (a >= 32 && a < 127) || (a === 9); // space -> DEL as well as horizontal tab
}

function trimQuotesIfString(query: string, token: Token): string {
  let start = token.index, end = token.index + token.length;
  if (token.type === TokenType.string) {
    // remove the quotes on either end:
    start++;
    end--;
  }
  return query.substring(start, end);
}

function lookupTokenInSearchableParams(query: string, token: Token): SearchableParamsEntry {
  const tt = token.type;
  if (tt !== TokenType.string && tt !== TokenType.symbol) return null; // @TODO maybe assert/throw?

  let str = trimQuotesIfString(query, token);
  str = str.toLowerCase();

  //@NOTE(nick) bleh, this is slower than just a hash-table lookup, but I wanted
  // to enable case-insensitive lookups, and if I manually lowercase or uppercase
  // all the object keys in the object itself, then I lose the nice property that 
  // all the keys relate to Keepa product finder query keys... oh well
  for (const [key, value] of Object.entries(SearchableParams)) {
    if (key.toLowerCase() === str) {
      return { key: key as keyof typeof SearchableParams, value };
    }
  }
  return null;
}

// this garbage is just to ensure you get a type-safe Object.entries, otherwise,
// the [key, value] pair is un-typed... @TOOD move somewhere else?
type ValueOf<T> = T[keyof T]
type Entries<T> = [keyof T, NonNullable<ValueOf<T>>][]
function objectEntries<T extends object>(obj: T): Entries<T> {
  return (Object.entries(obj) as Entries<T>).filter(entry => entry[1] !== undefined);
}
export function safeEntries<T extends object>(obj?: T): Entries<T> {
  if (!obj) {
    return objectEntries({});
  } else {
    return objectEntries(obj);
  }
}
